import React, { useEffect, useState, useCallback } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../store';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import Input from '../shared/TailwindComponents/Input';
import Textarea from '../shared/TailwindComponents/Textarea';
import Dropdown, { DropdownOption } from '../shared/TailwindComponents/Dropdown';
import Button from '../shared/TailwindComponents/Button';
import { fetchAllTeams } from '../../store/actions/organization/teamActions';
import { fetchOrgPeople } from '../../store/actions/OnboardingActions';
import { fetchMatters } from '../../store/actions/Matters/MatterActions';
import FileUploader from '../shared/TailwindComponents/FileUploader';
import DatePicker from 'react-datepicker';
import { useHistory } from 'react-router-dom';
import { PiCheckSquareOffsetLight } from 'react-icons/pi';
import { useTranslation } from 'react-i18next';
import apis from '../../utils/apis';
import ProfileImage from '../Header/ProfileImage';
import ApiLoader from '../ApiLoader';
import CollaboratorSelector from '../shared/CollaboratorSelector';

// Import validation system
import { createTaskSchema, sanitizeFormData, validateForm } from '../../utils/validation';
import { FieldError } from '../shared/FormComponents';
import { useLicenseGate } from '../../hooks/useLicenseGate';

interface AddTaskProps {
  onCancel?: () => void;
  onAdd?: () => void;
  teamId?: string;
}

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  user_id: number;
}

interface Collaborator {
  user_id: string | number;
  access: 'edit' | 'view';
}

interface TaskFormValues {
  TaskTitle: string;
  TaskDescription: string;
  TaskDeadline: string;
  MatterUUID: string;
  AssigneeType: string;
  AssigneeID: string;
  files: File[];
  status: string;
  priority: string;
}

const AddTask: React.FC<AddTaskProps> = ({ onCancel, onAdd, teamId }) => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const { checkSectionGate } = useLicenseGate();

  const [matterOptions, setMatterOptions] = useState<DropdownOption[]>([]);
  const [teamOptions, setTeamOptions] = useState<DropdownOption[]>([]);
  const [scheduleDate, setScheduleDate] = useState<Date | null>(null);
  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
  const [isCreatingTask, setIsCreatingTask] = useState(false);

  const teams = useSelector((state: RootState) => state.teams.teams || []);
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const collaboratorsUsers = useSelector((state: RootState) => state.orgPeople?.people || []);


  const loadInitialData = useCallback(async () => {
    const promises = [];

    promises.push(
      dispatch(fetchMatters({ page: 1, page_size: 100 }) as any)
        .then((resp: any) => {
          if (resp && resp.matters) {
            const formattedMatters = resp.matters.map((matter: any) => ({
              value: matter.uuid,
              label: matter.title,
            }));
            setMatterOptions(formattedMatters);
          }
        })
    );

    promises.push(dispatch(fetchOrgPeople()));

    promises.push(
      dispatch(fetchAllTeams())
        .then(resp => {
          if (resp && resp.teams) {
            const formattedTeams = resp.teams.map((team: any) => ({
              value: team.LegalTeamID,
              label: team.TeamName,
            }));
            setTeamOptions(formattedTeams);
          }
        })
    );

    await Promise.all(promises);
  }, [dispatch]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const validationSchema = Yup.object().shape({
    TaskTitle: Yup.string().required(t('Title is required')),
    TaskDescription: Yup.string().required(t('Description is required')),
    TaskDeadline: Yup.date().required(t('Deadline is required')),
    AssigneeType: Yup.string(),
    AssigneeID: Yup.string().when('AssigneeType', {
      is: (AssigneeType: any) => AssigneeType === 'people' || AssigneeType === 'team',
      then: (schema) => schema.required(t('Please select a responsible party')),
    }),
  });

  const formik = useFormik<TaskFormValues>({
    initialValues: {
      TaskTitle: '',
      TaskDescription: '',
      TaskDeadline: '',
      MatterUUID: '',
      AssigneeType: teamId ? "team" : '',
      AssigneeID: teamId || '',
      files: [] as File[],
      status: 'todo',
      priority: 'medium',
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      if (!checkSectionGate('tasks')) return;
      try {
        setIsCreatingTask(true);

        // Validate and sanitize form data
        const taskSchema = createTaskSchema();
        const validationResult = validateForm(taskSchema, {
          title: values.TaskTitle,
          description: values.TaskDescription,
          dueDate: values.TaskDeadline,
          status: values.status,
          priority: values.priority,
        }, false);

        if (!validationResult.isValid) {
          const errorMessages = Object.values(validationResult.errors)
            .flat()
            .join('; ');
          throw new Error(`Validation failed: ${errorMessages}`);
        }

        // Sanitize all form data
        const sanitizedValues = sanitizeFormData({
          title: values.TaskTitle,
          description: values.TaskDescription,
          due_date: values.TaskDeadline,
          status: values.status,
          priority: values.priority,
          matterUUID: values.MatterUUID,
          assigneeType: values.AssigneeType,
          assigneeId: values.AssigneeID,
        });

        // Prepare FormData for single consolidated request
        const formData = new FormData();

        // Add task fields with sanitized values
        formData.append('title', sanitizedValues.title || '');
        formData.append('description', sanitizedValues.description || '');
        formData.append('due_date', sanitizedValues.due_date || '');
        formData.append('status', sanitizedValues.status || '');
        formData.append('priority', sanitizedValues.priority || '');

        if (values.MatterUUID) {
          formData.append('matter_uuid', values.MatterUUID);
        }

        if (sanitizedValues.assigneeType === 'team' && sanitizedValues.assigneeId) {
          formData.append('TeamID', String(sanitizedValues.assigneeId));
        }

        if (sanitizedValues.assigneeType === 'people' && sanitizedValues.assigneeId) {
          formData.append('assigned_to_user_id', String(sanitizedValues.assigneeId));
        }

        if (collaborators.length > 0) {
          formData.append('is_shared', 'true');
          // Add collaborators with access levels
          const validCollaborators = collaborators
            .map(c => ({
              user_id: parseInt(String(c.user_id), 10),
              access: c.access
            }))
            .filter(c => !Number.isNaN(c.user_id));
          formData.append('collaborators', JSON.stringify(validCollaborators));
        }

        // Add files if any were selected - sanitize filenames
        if (values.files && values.files.length > 0) {
          for (const file of values.files) {
            formData.append('files', file);
          }
        }

        // Make single consolidated API call
        const response = await apis.post('/tasks/create_task', formData);

        if (!response.data || !response.data.uuid) {
          throw new Error('Task UUID not returned from API');
        }

        const taskUuid = response.data.uuid;

        dispatch(
          showFeedbackModal({
            modalType: 'success',
            message: t('Task created successfully'),
            showModal: true,
            duration: 2000,
          })
        );

        if (onAdd) onAdd();
        resetForm();

        // Redirect to task details page
        setTimeout(() => {
          history.push(`/tasks/${taskUuid}`);
        }, 500);
      } catch (error: any) {
        const errorMessage = error?.response?.data?.validation_errors
          ? Object.values(error.response.data.validation_errors).join(', ')
          : error?.response?.data?.error
          ? error.response.data.error
          : t('Could not add task. Please contact support.');

        dispatch(
          showFeedbackModal({
            modalType: 'error',
            duration: 3000,
            message: errorMessage,
            showModal: true,
          })
        );
      } finally {
        setIsCreatingTask(false);
      }
    },
  });

  const handleCancel = () => {
    if (onCancel) onCancel();
  };

  return (
    <div className="px-6 pb-6 bg-white dark:bg-gray-900 rounded-xl shadow-lg max-w-3xl w-full mx-auto mt-5 flex flex-col overflow-y-auto max-h-[calc(100vh-50px)] custom-scrollbar-main">
      <div className="sticky top-0 z-10 pt-6 bg-white dark:bg-gray-900 pb-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
        <div className="flex gap-2 items-center">
          <PiCheckSquareOffsetLight size={24} className="text-secondary-900" />
          <div className="block space-y-1">
            <h2 className="text-lg text-gray-900 dark:text-white flex items-center gap-2">
              {t('Create New Task')}
            </h2>
            <p className="text-xs text-gray-600 dark:text-gray-200">
              {t('Fill out this form to add your task.')}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="neutral" size="small" onClick={handleCancel}>
            {t('Cancel')}
          </Button>
        </div>
      </div>

      <form id="add-task-form" className="space-y-5 mt-6" onSubmit={formik.handleSubmit}>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Input
                label={t('Task Title')}
                type="text"
                name="TaskTitle"
                value={formik.values.TaskTitle}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder={t('Enter task title')}
                error={formik.touched.TaskTitle && formik.errors.TaskTitle ? formik.errors.TaskTitle : undefined}
                required
                className='text-xs'
              />
              <FieldError
                error={formik.touched.TaskTitle && formik.errors.TaskTitle ? String(formik.errors.TaskTitle) : undefined}
                visible={!!(formik.touched.TaskTitle && formik.errors.TaskTitle)}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-700 dark:text-gray-300 mb-2">
                {t('Deadline')}
              </label>
              <DatePicker
                selected={scheduleDate}
                onChange={(date: Date) => {
                  setScheduleDate(date);
                  formik.setFieldValue('TaskDeadline', date ? date.toISOString() : '');
                }}
                showTimeSelect
                timeFormat="HH:mm"
                timeIntervals={15}
                dateFormat="MMMM d, yyyy h:mm aa"
                placeholderText={t('Select a date and time')}
                className="block w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:focus:ring-primary-700 focus:border-primary-500 dark:focus:border-primary-700 transition duration-300 input text-xs"
              />
              {formik.touched.TaskDeadline && formik.errors.TaskDeadline && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{t(formik.errors.TaskDeadline)}</p>
              )}
            </div>
          </div>

          <div className="space-y-1">
            <label className="block text-xs text-gray-700 dark:text-gray-300 flex items-center gap-2">
              {t('Select Matter')}
            </label>
            <Dropdown
              options={matterOptions}
              onSelect={(option) => formik.setFieldValue('MatterUUID', option.value)}
              value={formik.values.MatterUUID}
              placeholder={t('Select a matter')}
            />
          </div>

          <div>
            <Textarea
              id="TaskDescription"
              name="TaskDescription"
              label={t('Task Description')}
              value={formik.values.TaskDescription}
              onChange={formik.handleChange}
              placeholder={t('Describe your task here')}
              error={formik.touched.TaskDescription && formik.errors.TaskDescription ? formik.errors.TaskDescription : undefined}
            />
            <FieldError
              error={formik.touched.TaskDescription && formik.errors.TaskDescription ? String(formik.errors.TaskDescription) : undefined}
              visible={!!(formik.touched.TaskDescription && formik.errors.TaskDescription)}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="block text-xs text-gray-700 dark:text-gray-300 flex items-center gap-2">
                {t('Priority')}
              </label>
              <Dropdown
                options={[
                  { value: 'low', label: t('Low') },
                  { value: 'medium', label: t('Medium') },
                  { value: 'high', label: t('High') },
                  { value: 'critical', label: t('Critical') },
                ]}
                onSelect={(option) => formik.setFieldValue('priority', option.value)}
                value={formik.values.priority}
                placeholder={t("Select priority")}
              />
            </div>
            <div className="space-y-1">
              <label className="block text-xs text-gray-700 dark:text-gray-300 flex items-center gap-2">
                {t('Status')}
              </label>
              <Dropdown
                options={[
                  { value: 'todo', label: t('To Do') },
                  { value: 'in_progress', label: t('In Progress') },
                  { value: 'on_hold', label: t('On Hold') },
                  { value: 'completed', label: t('Completed') },
                ]}
                onSelect={(option) => formik.setFieldValue('status', option.value)}
                value={formik.values.status}
                placeholder={t("Select status")}
              />
            </div>
          </div>

          {/* <CollaboratorSelector
            collaborators={collaborators}
            // onCollaboratorsChange={setCollaborators}
            title={t('Share with team members')}
            description={t('Add people to collaborate on this task')}
          /> */}
        </div>

        {/* <div className="space-y-3">
          <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 border-b border-gray-200 dark:border-gray-700 pb-2">
            {t('Deadline')}
          </h3>
          <DatePicker
            selected={formik.values.TaskDeadline ? new Date(formik.values.TaskDeadline) : null}
            onChange={(date) => formik.setFieldValue('TaskDeadline', date ? date.toISOString() : '')}
            showTimeSelect
            timeFormat="HH:mm"
            timeIntervals={15}
            dateFormat="MMM d, yyyy h:mm aa"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent"
            placeholderText={t('Select deadline')}
          />
        </div> */}

        <div className="space-y-3">
          <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 border-b border-gray-200 dark:border-gray-700 pb-2">
            {t('Attachments')}
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t('Import, upload, or drag and drop files to attach to this task (optional).')}
          </p>
          <FileUploader
            files={formik.values.files}
            onFilesChange={(newFiles: any) => formik.setFieldValue('files', newFiles)}
          />
        </div>

        <div className="flex justify-end gap-2">
          <Button
            variant="primary"
            size="small"
            type="submit"
            form="add-task-form"
            disabled={isCreatingTask}
            className="flex items-center gap-2"
          >
            {isCreatingTask && (
              <div className="flex items-center gap-2">
                <div className="animate-spin">
                  <svg className="w-4 h-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
                <span>{t('Creating task...')}</span>
              </div>
            )}
            {!isCreatingTask && t('Create Task')}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AddTask;
